/**
 * FormValidator - Reusable form validation component
 * 
 * Usage:
 * const validator = new FormValidator(formElement, options);
 * validator.validateField(field);
 * validator.validateAll();
 */
class FormValidator {
  constructor(form, options = {}) {
    this.form = form;
    this.options = {
      autoValidate: options.autoValidate || false,
      validateOnBlur: options.validateOnBlur !== false,
      validateOnInput: options.validateOnInput !== false,
      skipNoValidation: options.skipNoValidation !== false,
      showSuccessState: options.showSuccessState !== false,
      scrollToError: options.scrollToError !== false,
      customValidators: options.customValidators || {},
      fieldMessages: options.fieldMessages || {},
      fieldLabels: options.fieldLabels || {},
      ...options
    };

    if (this.options.autoValidate) {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Validation on blur
    if (this.options.validateOnBlur) {
      this.form.addEventListener('blur', (e) => {
        if (e.target.matches('input, textarea, select')) {
          e.target.setAttribute('data-touched', 'true');
          this.validateField(e.target);
        }
      }, true);
    }

    // Remove validation on input
    if (this.options.validateOnInput) {
      this.form.addEventListener('input', (e) => {
        if (e.target.matches('input, textarea, select')) {
          if (e.target.hasAttribute('data-touched')) {
            this.validateField(e.target);
          }
        }
      });
    }
  }

  validateField(field) {
    // Skip validation for fields with no-validation class
    if (this.options.skipNoValidation && field.classList.contains('no-validation')) {
      return true;
    }
    
    // Skip validation if field hasn't been touched (unless forced)
    if (!field.hasAttribute('data-touched') && !field.hasAttribute('data-force-validate')) {
      return true;
    }

    // Handle checkboxes differently
    const isCheckbox = field.type === 'checkbox';
    const value = isCheckbox ? field.checked : field.value.trim();
    let isValid = true;
    let errorMessage = '';

    // Check custom validators first
    const fieldName = field.name || field.id;
    if (this.options.customValidators[fieldName]) {
      const customResult = this.options.customValidators[fieldName](value, field);
      if (customResult !== true) {
        isValid = false;
        errorMessage = typeof customResult === 'string' ? customResult : 'Invalid value';
      }
    }

    // Built-in validations
    if (isValid) {
      // Required field validation
      if (field.hasAttribute('required') || field.required) {
        if (isCheckbox && !field.checked) {
          isValid = false;
          const fieldLabel = this.getFieldLabel(field);
          errorMessage = this.getFieldMessage(field, 'required') || `${fieldLabel} must be checked`;
        } else if (!isCheckbox && !value) {
          isValid = false;
          const fieldLabel = this.getFieldLabel(field);
          errorMessage = this.getFieldMessage(field, 'required') || `${fieldLabel} is required`;
        }
      }

      // Type-specific validations
      if (isValid && value) {
        switch (field.type) {
          case 'email':
            if (!this.isValidEmail(value)) {
              isValid = false;
              errorMessage = this.getFieldMessage(field, 'email') || 'Please enter a valid email address';
            }
            break;

          case 'url':
            if (!this.isValidUrl(value)) {
              isValid = false;
              errorMessage = this.getFieldMessage(field, 'url') || 'Please enter a valid URL';
            }
            break;

          case 'tel':
            if (!this.isValidPhone(value)) {
              isValid = false;
              errorMessage = this.getFieldMessage(field, 'tel') || 'Please enter a valid phone number';
            }
            break;

          case 'number':
            if (!this.isValidNumber(value, field)) {
              isValid = false;
              errorMessage = this.getFieldMessage(field, 'number') || 'Please enter a valid number';
            }
            break;
        }

        // Pattern validation
        if (isValid && field.hasAttribute('pattern')) {
          const pattern = new RegExp(field.pattern);
          if (!pattern.test(value)) {
            isValid = false;
            errorMessage = this.getFieldMessage(field, 'pattern') || field.title || 'Please match the requested format';
          }
        }

        // Length validations
        if (isValid) {
          if (field.hasAttribute('minlength') && value.length < parseInt(field.minLength)) {
            isValid = false;
            errorMessage = this.getFieldMessage(field, 'minlength') || `Minimum length is ${field.minLength} characters`;
          }
          if (field.hasAttribute('maxlength') && value.length > parseInt(field.maxLength)) {
            isValid = false;
            errorMessage = this.getFieldMessage(field, 'maxlength') || `Maximum length is ${field.maxLength} characters`;
          }
        }
      }
    }

    // Update field state
    if (!isValid) {
      this.showFieldError(field, errorMessage);
    } else {
      this.clearFieldError(field);
      // Don't show valid state for no-validation fields
      if (this.options.showSuccessState && value && !field.classList.contains('no-validation')) {
        this.showFieldValid(field);
      }
    }

    return isValid;
  }

  validateAll(forceShowErrors = false) {
    const fields = this.form.querySelectorAll('input, textarea, select');
    let allValid = true;
    const invalidFields = [];

    fields.forEach(field => {
      if (forceShowErrors) {
        field.setAttribute('data-touched', 'true');
      }
      if (!this.validateField(field)) {
        allValid = false;
        invalidFields.push(field);
      }
    });

    if (!allValid && this.options.scrollToError && invalidFields.length > 0) {
      invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return { valid: allValid, invalidFields };
  }

  showFieldError(field, message) {
    // Remove any existing error
    this.clearFieldError(field);

    // Add error class
    field.classList.add('is-invalid');
    field.classList.remove('is-valid');
    field.setAttribute('aria-invalid', 'true');

    // Special handling for distribution_agreement - no error message, just red label
    if (field.id === 'distribution_agreement') {
      return;
    }

    // Create and insert error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'field-error-message';
    errorDiv.textContent = message;
    errorDiv.setAttribute('role', 'alert');

    // Insert after the field (or after input group if exists)
    const inputGroup = field.closest('.input-group');
    const insertAfter = inputGroup || field;
    insertAfter.parentNode.insertBefore(errorDiv, insertAfter.nextSibling);
  }

  clearFieldError(field) {
    field.classList.remove('is-invalid');
    field.removeAttribute('aria-invalid');

    // Remove error message
    const inputGroup = field.closest('.input-group');
    const container = inputGroup || field;
    const errorMessage = container.parentNode.querySelector('.field-error-message');
    if (errorMessage) {
      errorMessage.remove();
    }
  }

  showFieldValid(field) {
    // Don't add valid class to fields with no-validation
    if (!field.classList.contains('no-validation')) {
      field.classList.add('is-valid');
      field.setAttribute('aria-invalid', 'false');
    }
  }

  // Validation helper methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  isValidPhone(phone) {
    // Loose phone validation - just check for at least 4 digits
    // Allow any combination of digits, spaces, dashes, plus signs, parentheses
    const phoneRegex = /^[\d\s\-\+\(\)\.ext]+$/i;
    const digitsOnly = phone.replace(/\D/g, '');
    // Require at least 4 digits (very loose validation)
    return phoneRegex.test(phone) && digitsOnly.length >= 4;
  }

  isValidNumber(value, field) {
    const num = parseFloat(value);
    if (isNaN(num)) return false;

    if (field.hasAttribute('min') && num < parseFloat(field.min)) return false;
    if (field.hasAttribute('max') && num > parseFloat(field.max)) return false;
    if (field.hasAttribute('step')) {
      const step = parseFloat(field.step);
      const min = field.hasAttribute('min') ? parseFloat(field.min) : 0;
      if ((num - min) % step !== 0) return false;
    }

    return true;
  }

  // Helper methods
  getFieldLabel(field) {
    const fieldName = field.name || field.id;
    
    // Check for custom label first
    if (this.options.fieldLabels[fieldName]) {
      return this.options.fieldLabels[fieldName];
    }
    
    // First try to get label from associated label element
    if (field.labels?.[0]) {
      return field.labels[0].textContent.replace('*', '').trim();
    }
    
    // For multiselect, look for label in parent container
    const container = field.closest('.form-group') || field.closest('[id$="-container"]');
    if (container) {
      const label = container.querySelector('label');
      if (label) {
        return label.textContent.replace('*', '').trim();
      }
    }
    
    // Clean up field name (e.g., "genres[]" -> "Genres")
    if (field.name) {
      return field.name
        .replace(/\[\]$/, '') // Remove array brackets
        .replace(/[_-]/g, ' ') // Replace underscores/hyphens with spaces
        .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
    }
    
    // Fallback to ID
    if (field.id) {
      return field.id
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return 'This field';
  }

  getFieldMessage(field, type) {
    const fieldName = field.name || field.id;
    return field.dataset[`error${type.charAt(0).toUpperCase() + type.slice(1)}`] ||
           this.options.fieldMessages[fieldName]?.[type];
  }

  // Public methods for manual control
  markFieldTouched(field) {
    field.setAttribute('data-touched', 'true');
  }

  markAllFieldsTouched() {
    const fields = this.form.querySelectorAll('input, textarea, select');
    fields.forEach(field => this.markFieldTouched(field));
  }

  reset() {
    const fields = this.form.querySelectorAll('input, textarea, select');
    fields.forEach(field => {
      field.removeAttribute('data-touched');
      field.removeAttribute('data-force-validate');
      this.clearFieldError(field);
      field.classList.remove('is-valid');
    });
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FormValidator;
} else if (typeof window !== 'undefined') {
  window.FormValidator = FormValidator;
}